import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import Icons from '@shared/components/core/images/Icons';
import { BodyMedium, TitleMedium } from '@shared/components/core/text/Typography';
import { IAlertNotificationProps } from '@shared/models/components/IAlertNotificationProps';
import {
    NotificationAccentWrap,
    NotificationContent,
    NotificationSubtitleAction,
} from './CommonAlertNotificationStyles';

/**
 * InternalNotificationCard is a component that displays a notification card with a title, subtitle, and icon.
 *
 * - `default`: allows unstyled button and text buttons to be used for constructing custom notification cards
 * - `accent` & `info`: uses the secondary colors and should be used for informational notifications
 * - `warning`: uses the warning color should be used for warning notifications
 * - `error`: uses the error color should be used for error notifications
 * - `success`: uses the success color should be used for success notifications
 *
 *  */

const InternalNotificationCard: React.FC<IAlertNotificationProps> = ({
    color = 'default',
    title,
    subtitle,
    subtitleAction,
    icon,
    children,
}) => {
    const theme = useTheme();

    if (!title || !subtitle) return null;
    return (
        <NotificationAccentWrap $color={color}>
            <FlexWrap>
                {icon && <Icons name={icon} color={theme.banner[color].icon} />}
                <TitleMedium as="h2">{title}</TitleMedium>
            </FlexWrap>
            <NotificationSubtitleAction $color={color}>
                {subtitle && <StyledBodyMedium>{subtitle}</StyledBodyMedium>}
                {subtitleAction}
            </NotificationSubtitleAction>
            <NotificationContent $color={color}>{children}</NotificationContent>
        </NotificationAccentWrap>
    );
};

const FlexWrap = styled.div`
    display: flex;
    flex-direction: row;
    gap: 1rem;
    align-items: center;
`;

const StyledBodyMedium = styled(BodyMedium)`
    display: inline;
`;

export default InternalNotificationCard;
